import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Icon } from "semantic-ui-react";
import "./MoleculeStyles.scss";
import SectionHeading from "./SectionHeading";
import i18nstring, { strings } from "../../i18n";
import { Button } from "../atoms/Button";
import ConfirmationPopup from "../atoms/ConfirmationPopup";
import PickupInstructionCard from "./PickupInstructionCard";
import PickupInstructionEditorModal from "../organisms/PickupInstructionEditorModal";
import PickupStateControls from "./PickupStateControls";
import {
  buildInitialPickupStates,
  createEmptyInstruction,
  EMPTY_EDITOR_ERRORS,
  normalizeInstruction,
  normalizeZips
} from "../../utils/PickupInstructions";

export default function PickupInstructionsZips({
  zips = [],
  locales = [],
  pickupInstructions = [],
  onSaveInstruction,
  onDeleteInstruction,
  onSavePickupStates,
  isSavingInstructions = false,
  isSavingPickupStates = false,
  isLoadingInstructions = false,
  hideDivider = false
}) {
  const normalizedZips = useMemo(() => normalizeZips(zips), [zips]);
  const timeWindowRef = useRef(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [draftInstruction, setDraftInstruction] = useState(createEmptyInstruction());
  const [editorErrors, setEditorErrors] = useState(EMPTY_EDITOR_ERRORS);
  const [instructionToDelete, setInstructionToDelete] = useState(null);
  const [pickupStates, setPickupStates] = useState({});
  const [expandedInstructions, setExpandedInstructions] = useState({});

  const dayLabels = useMemo(() => i18nstring(strings.DAY_LABELS) || {}, []);
  const normalizedLocales = useMemo(() => (Array.isArray(locales) ? locales : []), [locales]);

  const localeMap = useMemo(
    () =>
      new Map(
        normalizedLocales
          .filter(locale => locale && locale.zipCode !== undefined)
          .map(locale => [String(locale.zipCode), locale])
      ),
    [normalizedLocales]
  );

  const resetEditor = useCallback(() => {
    setDraftInstruction(createEmptyInstruction());
    setEditorErrors(EMPTY_EDITOR_ERRORS);
  }, []);

  const updateDraftInstruction = useCallback((changes, fieldsToClear = []) => {
    setDraftInstruction(currentInstruction => ({
      ...currentInstruction,
      ...changes
    }));

    if (fieldsToClear.length > 0) {
      setEditorErrors(currentErrors => ({
        ...currentErrors,
        ...fieldsToClear.reduce((nextErrors, field) => {
          nextErrors[field] = "";
          return nextErrors;
        }, {})
      }));
    }
  }, []);

  useEffect(() => {
    setPickupStates(buildInitialPickupStates(normalizedZips, localeMap));
  }, [normalizedZips, localeMap]);

  const instructionCards = useMemo(() => {
    return (pickupInstructions || [])
      .filter(instruction => instruction && instruction.active !== false)
      .map((instruction, index) =>
        normalizeInstruction({
          ...instruction,
          draftKey: instruction.draftKey || instruction.id || `instruction-${index}`
        })
      )
      .sort(
        (left, right) =>
          (right.updatedAt || right.createdAt || 0) -
          (left.updatedAt || left.createdAt || 0)
      );
  }, [pickupInstructions]);

  const zipOptions = useMemo(() => {
    const selectedInOtherInstructions = new Set(
      instructionCards
        .filter(instruction => instruction.draftKey !== draftInstruction.draftKey)
        .flatMap(instruction => (instruction.zips || []).map(zip => String(zip)))
    );

    return normalizedZips
      .filter(
        zip =>
          !selectedInOtherInstructions.has(String(zip)) ||
          (draftInstruction.zips || []).includes(String(zip))
      )
      .map(zip => ({
        key: zip,
        text: zip,
        value: zip
      }));
  }, [draftInstruction.draftKey, draftInstruction.zips, instructionCards, normalizedZips]);

  const hasPickupStateChanges = useMemo(
    () =>
      normalizedZips.some(
        zip =>
          (pickupStates[String(zip)] ?? false) !==
          (localeMap.get(String(zip))?.pickupEnabled === true)
      ),
    [localeMap, normalizedZips, pickupStates]
  );

  const handleTogglePickupState = useCallback((zipCode, checked) => {
    setPickupStates(currentState => ({
      ...currentState,
      [String(zipCode)]: checked
    }));
  }, []);

  const handleToggleInstructionExpansion = useCallback((instructionKey) => {
    setExpandedInstructions(currentState => ({
      ...currentState,
      [instructionKey]: !currentState[instructionKey]
    }));
  }, []);

  const handleSavePickupStateChanges = useCallback(async () => {
    if (
      !onSavePickupStates ||
      isSavingInstructions ||
      isSavingPickupStates ||
      !hasPickupStateChanges
    ) {
      return;
    }

    const changedStates = normalizedZips
      .map(zip => String(zip))
      .filter(
        zip =>
          (pickupStates[zip] ?? false) !==
          (localeMap.get(zip)?.pickupEnabled === true)
      )
      .map(zip => ({
        zipCode: zip,
        pickupEnabled: pickupStates[zip] === true
      }));

    await onSavePickupStates(changedStates);
  }, [
    hasPickupStateChanges,
    isSavingInstructions,
    isSavingPickupStates,
    localeMap,
    normalizedZips,
    onSavePickupStates,
    pickupStates
  ]);

  const openCreateModal = useCallback(() => {
    setDraftInstruction({
      ...createEmptyInstruction(),
      draftKey: `draft-${Date.now()}`
    });
    setEditorErrors(EMPTY_EDITOR_ERRORS);
    setIsEditorOpen(true);
  }, []);

  const openEditModal = useCallback((instruction) => {
    setDraftInstruction(normalizeInstruction(instruction));
    setEditorErrors(EMPTY_EDITOR_ERRORS);
    setIsEditorOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    if (isSavingInstructions) {
      return;
    }

    setIsEditorOpen(false);
    resetEditor();
  }, [isSavingInstructions, resetEditor]);

  const syncDraftSchedule = useCallback(({ deliverySchedule } = {}) => {
    updateDraftInstruction(
      {
        deliverySchedule: {
          AM: deliverySchedule?.AM || [],
          PM: deliverySchedule?.PM || []
        }
      },
      ["schedule"]
    );
  }, [updateDraftInstruction]);

  const handleZipChange = useCallback((selectedZips) => {
    updateDraftInstruction({ zips: normalizeZips(selectedZips) });
  }, [updateDraftInstruction]);

  const handleInstructionsChange = useCallback((deliveryInstructions) => {
    updateDraftInstruction(
      { deliveryInstructions },
      ["deliveryInstructions"]
    );
  }, [updateDraftInstruction]);

  const handleSaveInstruction = useCallback(async () => {
    if (!onSaveInstruction || isSavingInstructions) {
      return;
    }

    const selectedDays = await timeWindowRef.current?.setSchedule();
    const normalizedSelectedZips = normalizeZips(draftInstruction.zips);
    const instructionPayload = {
      deliveryInstructions: (draftInstruction.deliveryInstructions || "").trim(),
      zips: normalizedSelectedZips,
      deliverySchedule: selectedDays || {
        AM: draftInstruction.deliverySchedule?.AM || [],
        PM: draftInstruction.deliverySchedule?.PM || []
      }
    };

    const hasDeliveryInstructions = !!instructionPayload.deliveryInstructions;
    const hasScheduleSelection =
      instructionPayload.deliverySchedule.AM.length > 0 ||
      instructionPayload.deliverySchedule.PM.length > 0;

    if (!hasDeliveryInstructions && !hasScheduleSelection) {
      setEditorErrors({
        deliveryInstructions: i18nstring(
          strings.PICKUP_INSTRUCTION_DELIVERY_INSTRUCTIONS_REQUIRED
        ),
        schedule: i18nstring(strings.PICKUP_INSTRUCTION_SCHEDULE_REQUIRED)
      });
      return;
    }

    setEditorErrors(EMPTY_EDITOR_ERRORS);

    const saveSucceeded = await onSaveInstruction(
      instructionPayload,
      draftInstruction.id || null
    );

    if (saveSucceeded) {
      closeEditor();
    }
  }, [closeEditor, draftInstruction, isSavingInstructions, onSaveInstruction]);

  const handleDeleteInstruction = useCallback(async () => {
    if (!instructionToDelete?.id || !onDeleteInstruction || isSavingInstructions) {
      setInstructionToDelete(null);
      return false;
    }

    const deleteSucceeded = await onDeleteInstruction(instructionToDelete.id);
    if (deleteSucceeded) {
      setInstructionToDelete(null);
      return true;
    }

    return false;
  }, [instructionToDelete, isSavingInstructions, onDeleteInstruction]);

  const renderAddButton = () => (
    <Button
      className="locations_create_button pickup_instructions_add_button"
      onClick={openCreateModal}
    >
      <Icon name="plus" />
      {i18nstring(strings.ADD_PICKUP_INSTRUCTION)}
    </Button>
  );

  return (
    <>
      <div
        className={`pickup_dropoff_section pickup_instructions_section ${
          hideDivider ? "locations_table_layout_no_border" : ""
        }`}
      >
        <div className="locations_table_sidebar">
          <SectionHeading title={i18nstring(strings.PICKUP_INSTRUCTIONS)} />
        </div>
        <div className="locations_table_divider" />
        <div className="pickup_dropoff_content">
          {normalizedZips.length > 0 ? (
            <div className="pickup_instructions_shell">
              <PickupStateControls
                zips={normalizedZips}
                pickupStates={pickupStates}
                hasChanges={hasPickupStateChanges}
                isSaving={isSavingPickupStates}
                onToggle={handleTogglePickupState}
                onSave={handleSavePickupStateChanges}
              />

              <div className="pickup_instructions_subheader">
                <p className="pickup_instructions_subheader_title">{i18nstring(strings.PICKUP_INSTRUCTIONS)}</p>
                <div className="offers_sub_header">{i18nstring(strings.PICKUP_INSTRUCTIONS_INFO)}</div>
              </div>

              {instructionCards.length > 0 ? (
                <div className="pickup_instruction_cards_grid">
                  {instructionCards.map((instruction, index) => {
                    const instructionKey =
                      instruction.id || instruction.draftKey || `pickup-instruction-${index}`;

                    return (
                      <PickupInstructionCard
                        key={instructionKey}
                        instruction={instruction}
                        instructionKey={instructionKey}
                        dayLabels={dayLabels}
                        isExpanded={!!expandedInstructions[instructionKey]}
                        onToggleExpansion={handleToggleInstructionExpansion}
                        onEdit={openEditModal}
                        onDelete={setInstructionToDelete}
                      />
                    );
                  })}
                </div>
              ) : isLoadingInstructions ? (
                <div className="pickup_instructions_empty pickup_instructions_empty_state pickup_instructions_empty_state_card">
                  <span>{i18nstring(strings.LOADING_PICKUP_INSTRUCTIONS)}</span>
                </div>
              ) : (
                <div className="pickup_instructions_empty pickup_instructions_empty_state pickup_instructions_empty_state_card">
                  <span>{i18nstring(strings.NO_PICKUP_INSTRUCTIONS_ADDED)}</span>
                  {renderAddButton()}
                </div>
              )}
            </div>
          ) : (
            <div className="pickup_instructions_empty">
              {i18nstring(strings.NO_ZIPS_AVAILABLE)}
            </div>
          )}
        </div>
      </div>

      {instructionCards.length > 0 && (
        <div className="pickup_dropoff_section pickup_instructions_section pickup_instructions_add_row_section locations_table_layout_no_border">
          <div className="locations_table_sidebar" />
          <div className="locations_table_divider" />
          <div className="pickup_dropoff_content">
            <div className="locations_create_button_row">{renderAddButton()}</div>
          </div>
        </div>
      )}

      <PickupInstructionEditorModal
        isOpen={isEditorOpen}
        draftInstruction={draftInstruction}
        zipOptions={zipOptions}
        editorErrors={editorErrors}
        isSaving={isSavingInstructions}
        timeWindowRef={timeWindowRef}
        onClose={closeEditor}
        onSave={handleSaveInstruction}
        onZipChange={handleZipChange}
        onInstructionsChange={handleInstructionsChange}
        onScheduleChange={syncDraftSchedule}
      />

      {instructionToDelete && (
        <ConfirmationPopup
          title={i18nstring(strings.DELETE_PICKUP_INSTRUCTION)}
          message={i18nstring(strings.DELETE_PICKUP_INSTRUCTION_CONFIRMATION_MESSAGE)}
          primaryButtonText={i18nstring(strings.DELETE)}
          onPrimaryButtonClicked={handleDeleteInstruction}
          open={!!instructionToDelete}
          setOpen={() => setInstructionToDelete(null)}
          isProcessing={isSavingInstructions}
        />
      )}
    </>
  );
}